import i18next from 'i18next';
import { initReactI18next } from 'react-i18next';

import { PortalConfig } from 'src/context/portalConfigContext';
import { getCustomizedLabels } from 'src/utils/getCustomizedLabels';

import translation from './translations/en-US.json';

export interface I18NLoaderProps {
  customLabels: PortalConfig['features']['customLabels'] | null;
  isAssembly: boolean;
}

const DEFAULT_LANGUAGE = 'en';
const DEFAULT_NAMESPACE = 'translation';

export const i18n = i18next.use(initReactI18next);

// Initialize i18n
void i18n.init({
  lng: 'en',
  interpolation: {
    escapeValue: false, // react already safes from xss
  },
  react: {
    useSuspense: false,
  },
});

// Add the "lowercase" formatter
i18n.services.formatter?.add('lowercase', (value: string) =>
  value.toLowerCase(),
);

// Portal-customized terms are free text, so a term that leads a sentence has to
// be capitalized at the point of use rather than assumed capitalized.
i18n.services.formatter?.add(
  'capitalize',
  (value: string) => value.charAt(0).toUpperCase() + value.slice(1),
);

/**
 * Check if the i18n resources are loaded
 */
export function isI18nResourceLoaded(): boolean {
  return i18n.hasResourceBundle(DEFAULT_LANGUAGE, DEFAULT_NAMESPACE);
}

/**
 * Load the i18n resources
 */
export function loadI18nResources(props: I18NLoaderProps): void {
  i18n.addResourceBundle(
    DEFAULT_LANGUAGE,
    DEFAULT_NAMESPACE,
    {
      ...translation,
      ...getCustomizedLabels(props),
    },
    true,
  );
}

/**
 * Initialize the i18n resources (or reload if already loaded)
 */
export function initI18nResources(props: I18NLoaderProps): void {
  if (!isI18nResourceLoaded()) {
    loadI18nResources(props);
  } else {
    i18n.removeResourceBundle(DEFAULT_LANGUAGE, DEFAULT_NAMESPACE);
    loadI18nResources(props);
  }
}
